import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import { Plane, Plus, Loader2, Check, X, Trash2, Calendar as CalIcon, User as UserIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
};

const TYPE_LABELS = (lang) => ({
    annual: t(lang, "annualLeave"),
    special: t(lang, "specialLeave"),
    unpaid: t(lang, "unpaidLeave"),
    sick: t(lang, "sickLeave"),
});

export default function VacationsPage() {
    const { lang, user } = useAuth();
    const [items, setItems] = useState([]);
    const [quota, setQuota] = useState(null);
    const [whoOut, setWhoOut] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOpen, setNewOpen] = useState(false);

    const isPriv = user?.role === "admin" || user?.role === "master";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [req, qu, out, emp] = await Promise.all([
                api.get("/vacation/requests"),
                api.get("/vacation/quota"),
                api.get("/vacation/who-is-out"),
                api.get("/employees"),
            ]);
            setItems(req.data);
            setQuota(qu.data);
            setWhoOut(out.data);
            setEmployees(emp.data);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const decide = async (id, decision) => {
        const note = decision === "rejected" ? (window.prompt(lang === "de" ? "Begründung (optional):" : "Note:") || "") : "";
        try {
            await api.post(`/vacation/requests/${id}/decide`, { decision, note });
            toast.success(t(lang, decision));
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const remove = async (id) => {
        if (!window.confirm(lang === "de" ? "Antrag löschen?" : "Delete?")) return;
        try {
            await api.delete(`/vacation/requests/${id}`);
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    return (
        <div className="px-4 sm:px-8 py-8 max-w-5xl" data-testid="vacations-page">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
                        <Plane className="w-3 h-3" /> {t(lang, "vacations")}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-1">{t(lang, "vacations")}</h1>
                </div>
                <Button onClick={() => setNewOpen(true)} data-testid="new-vacation-btn" className="bg-neutral-900 hover:bg-neutral-800">
                    <Plus className="w-4 h-4 mr-1.5" /> {t(lang, "newVacationRequest")}
                </Button>
            </div>

            {/* Quota card */}
            {quota && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="vacation-quota">
                    <QuotaCard label={t(lang, "quota")} value={quota.quota} unit={t(lang, "workdays")} color="neutral" />
                    <QuotaCard label={t(lang, "used")} value={quota.used} unit={t(lang, "workdays")} color="emerald" />
                    <QuotaCard label={t(lang, "pending")} value={quota.pending} unit={t(lang, "workdays")} color="amber" />
                    <QuotaCard label={t(lang, "remaining")} value={quota.remaining} unit={t(lang, "workdays")} color="blue" highlight />
                </div>
            )}

            {/* Who's out */}
            {whoOut.length > 0 && (
                <section className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="who-is-out">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1.5">
                        <CalIcon className="w-3 h-3" /> {t(lang, "whoIsOut")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {whoOut.map((v) => (
                            <span key={v.id} className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-300 px-2.5 py-1 rounded-full" data-testid={`out-${v.user_id}`}>
                                <UserIcon className="w-3 h-3" />
                                <span className="font-semibold">{v.user_name}</span>
                                <span className="text-neutral-500">({new Date(v.end_date).toLocaleDateString(LOCALES[lang])})</span>
                            </span>
                        ))}
                    </div>
                </section>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-lg py-16 text-center">
                    <Plane className="w-12 h-12 mx-auto text-neutral-300 mb-2" />
                    <p className="text-neutral-500">{t(lang, "noTodos")}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((v) => {
                        const labels = TYPE_LABELS(lang);
                        return (
                            <div key={v.id} data-testid={`vacation-${v.id}`} className="p-4 bg-white border border-neutral-200 rounded-lg hover:shadow-sm transition">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${STATUS_STYLES[v.status]}`}>
                                                {t(lang, v.status)}
                                            </span>
                                            <span className="text-xs text-neutral-500 uppercase font-semibold tracking-wider">{labels[v.type]}</span>
                                            {isPriv && <span className="text-xs text-neutral-600">· {v.user_name}</span>}
                                        </div>
                                        <p className="text-sm font-medium mt-1.5">
                                            {new Date(v.start_date).toLocaleDateString(LOCALES[lang])} → {new Date(v.end_date).toLocaleDateString(LOCALES[lang])}
                                            <span className="ml-2 text-xs text-neutral-500">({v.days} {t(lang, "workdays")})</span>
                                        </p>
                                        {v.reason && <p className="text-xs text-neutral-600 mt-1">{v.reason}</p>}
                                        {v.decision_note && (
                                            <p className="text-xs text-neutral-600 mt-1 italic">
                                                {lang === "de" ? "Bemerkung" : "Note"}: {v.decision_note} —{" "}
                                                {v.decision_by_name && <span className="font-semibold">{v.decision_by_name}</span>}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {isPriv && v.status === "pending" && (
                                            <>
                                                <Button size="sm" onClick={() => decide(v.id, "approved")} data-testid={`approve-${v.id}`} className="bg-brand-500 hover:bg-brand-600 h-8 px-2.5">
                                                    <Check className="w-3.5 h-3.5 mr-1" /> {t(lang, "approve")}
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => decide(v.id, "rejected")} data-testid={`reject-${v.id}`} className="border-red-300 text-red-700 hover:bg-red-50 h-8 px-2.5">
                                                    <X className="w-3.5 h-3.5 mr-1" /> {t(lang, "reject")}
                                                </Button>
                                            </>
                                        )}
                                        {(v.user_id === user?.id && v.status === "pending") || isPriv ? (
                                            <button onClick={() => remove(v.id)} data-testid={`del-vac-${v.id}`} className="text-neutral-400 hover:text-red-600 p-1.5">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <NewVacationDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} employees={employees} userId={user?.id} lang={lang} />
        </div>
    );
}

function QuotaCard({ label, value, unit, color, highlight }) {
    const palette = {
        neutral: "bg-neutral-50 text-neutral-800 border-neutral-200",
        emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        blue: "bg-blue-50 text-blue-800 border-blue-200",
    };
    return (
        <div className={`p-4 rounded-lg border ${palette[color]} ${highlight ? "ring-2 ring-blue-200" : ""}`}>
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
            <p className="text-2xl font-bold mt-1 font-mono">{value}</p>
            <p className="text-[10px] opacity-60">{unit}</p>
        </div>
    );
}

function NewVacationDialog({ open, onClose, onCreated, employees, userId, lang }) {
    const [type, setType] = useState("annual");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [reason, setReason] = useState("");
    const [substitute, setSubstitute] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setType("annual"); setStart(""); setEnd(""); setReason(""); setSubstitute(""); }
    }, [open]);

    const days = (() => {
        if (!start || !end) return 0;
        const s = new Date(start), e = new Date(end);
        if (e < s) return 0;
        let d = 0; let cur = new Date(s);
        while (cur <= e) {
            const wd = cur.getDay();
            if (wd >= 1 && wd <= 5) d++;
            cur.setDate(cur.getDate() + 1);
        }
        return d;
    })();

    const submit = async () => {
        if (!start || !end) return;
        setBusy(true);
        try {
            await api.post("/vacation/requests", {
                type, start_date: start, end_date: end,
                substitute_user_id: substitute || null,
                reason,
            });
            toast.success(t(lang, "create"));
            onCreated();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="new-vacation-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "newVacationRequest")}</DialogTitle>
                    <DialogDescription className="sr-only">Urlaubsantrag</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "vacationType")}</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="mt-1.5" data-testid="vac-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="annual">{t(lang, "annualLeave")}</SelectItem>
                                <SelectItem value="special">{t(lang, "specialLeave")}</SelectItem>
                                <SelectItem value="unpaid">{t(lang, "unpaidLeave")}</SelectItem>
                                <SelectItem value="sick">{t(lang, "sickLeave")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "startDate")}</Label>
                            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="vac-start" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "endDate")}</Label>
                            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="vac-end" className="mt-1.5" />
                        </div>
                    </div>
                    {days > 0 && (
                        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                            <strong>{days}</strong> {t(lang, "workdays")} ({lang === "de" ? "Wochenenden ausgenommen" : "weekends excluded"})
                        </p>
                    )}
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "substitute")} <span className="text-neutral-400 normal-case font-normal">({lang === "de" ? "optional" : "optional"})</span></Label>
                        <Select value={substitute || "_none"} onValueChange={(v) => setSubstitute(v === "_none" ? "" : v)}>
                            <SelectTrigger className="mt-1.5" data-testid="vac-substitute"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_none">—</SelectItem>
                                {employees.filter((u) => u.id !== userId).map((u) => (
                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "reason")}</Label>
                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="vac-reason" className="mt-1.5" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !start || !end || days <= 0} data-testid="vac-create-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
